import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';

/** Client-side filter state applied over the already-fetched snapshot. */
export interface DescalceFilterState {
  search: string;
  minDescalce: number;
  onlyGroupsOn: boolean;
  hideResolving: boolean;
  onlyProd: boolean;
  onlyTest: boolean;
  onlyDivergence: boolean;
}

export const DEFAULT_DESCALCE_FILTERS: DescalceFilterState = {
  search: '',
  minDescalce: 0.5,
  onlyGroupsOn: false,
  hideResolving: false,
  onlyProd: false,
  onlyTest: false,
  onlyDivergence: false
};

function matchesSearch(row: OpenerDescalceRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = `${row.pairName ?? ''} ${row.group.name ?? ''}`.toLowerCase();
  return haystack.includes(q);
}

/**
 * A row with no numeric descalce yet (`venue === null` — unconfigured /
 * starting / error account) is never hidden behind the numeric floor: it is
 * exactly the "unknown" the screen exists to surface, and a 0.5 default
 * floor would otherwise make it vanish silently.
 */
function matchesMinDescalce(row: OpenerDescalceRow, min: number): boolean {
  if (min <= 0) return true;
  if (!row.venue) return true;
  return row.venue.descalce >= min;
}

function matchesDivergence(row: OpenerDescalceRow): boolean {
  if (row.divergence == null) return false;
  return Math.abs(row.divergence.total) > IMBALANCE_TOLERANCE;
}

export function applyDescalceFilters(
  rows: OpenerDescalceRow[],
  filters: DescalceFilterState
): OpenerDescalceRow[] {
  return rows.filter(row => {
    if (!matchesSearch(row, filters.search)) return false;
    if (!matchesMinDescalce(row, filters.minDescalce)) return false;
    if (filters.onlyGroupsOn && row.group.podsDesired !== 'on') return false;
    if (filters.hideResolving && row.venue?.resolving === true) return false;
    if (filters.onlyProd && row.account !== 'prod') return false;
    if (filters.onlyTest && row.account !== 'test') return false;
    if (filters.onlyDivergence && !matchesDivergence(row)) return false;
    return true;
  });
}

export interface DescalceTotals {
  rowCount: number;
  /** Sum of `venue.descalce` over NON-ambiguous rows only. */
  totalDescalce: number;
  ambiguousCount: number;
  resolvingCount: number;
}

/**
 * Header-strip totals. Ambiguous rows are excluded from the summed total —
 * each ambiguous claimant carries the FULL market size (never an invented
 * split, per the sweeper's own invariant), so summing them in would double
 * (or N-fold) count contracts that live on a single venue position.
 */
export function computeDescalceTotals(
  rows: OpenerDescalceRow[]
): DescalceTotals {
  let totalDescalce = 0;
  let ambiguousCount = 0;
  let resolvingCount = 0;
  for (const row of rows) {
    if (!row.venue) continue;
    if (row.venue.ambiguous) {
      ambiguousCount++;
      continue;
    }
    totalDescalce += row.venue.descalce;
    if (row.venue.resolving) resolvingCount++;
  }
  return { rowCount: rows.length, totalDescalce, ambiguousCount, resolvingCount };
}
